// ---------------------------------------------------------------------------
// Breadcrumb — the trail of the new layout's top bar (AL-1891)
// ---------------------------------------------------------------------------
// One trail for the pages that had their own copy: the Persona and the Lens
// (Figma: DM Sans 13/500, past crumbs at 30% with the "›" glyph) and the
// account pages (CT-0: 14px, muted crumbs, chevron). The last crumb is the
// current page; the others link back when they have somewhere to go.
// ---------------------------------------------------------------------------

import type React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Crumb {
  readonly label: React.ReactNode;
  /** Where the crumb links to; the current page and plain crumbs have none. */
  readonly to?: string;
  /** Full text when the label is shortened. */
  readonly title?: string;
}

export interface BreadcrumbProps {
  /** Past crumbs first, the current page last. */
  readonly items: readonly Crumb[];
  readonly ariaLabel: string;
  /** `topbar`: the Figma trail of the Persona and the Lens; `settings`: CT-0's. */
  readonly variant?: 'topbar' | 'settings';
  readonly className?: string;
  readonly testId?: string;
}

const STYLES = {
  topbar: {
    nav: 'flex min-w-0 items-center gap-[4px] font-dm-sans text-[13px] font-medium text-v11-text-primary/30',
    past: 'truncate',
    link: 'truncate transition-colors hover:text-v11-text-primary focus:outline-hidden focus-visible:underline',
    current: 'truncate text-v11-text-primary',
  },
  settings: {
    nav: 'flex items-center gap-1 text-sm',
    past: 'text-v11-text-muted',
    link: 'text-v11-text-muted transition-colors hover:text-v11-text-primary',
    current: 'font-medium text-v11-text-primary',
  },
} as const;

function Separator({ variant }: { readonly variant: 'topbar' | 'settings' }) {
  return variant === 'settings' ? (
    <ChevronRight className="h-3.5 w-3.5 text-v11-text-muted" aria-hidden="true" />
  ) : (
    <span className="shrink-0" aria-hidden="true">
      ›
    </span>
  );
}

export function Breadcrumb({
  items,
  ariaLabel,
  variant = 'topbar',
  className,
  testId,
}: BreadcrumbProps) {
  const style = STYLES[variant];
  const last = items.length - 1;

  return (
    <nav aria-label={ariaLabel} data-testid={testId} className={cn(style.nav, className)}>
      {items.map((item, i) => (
        <span key={i} className="contents">
          {i > 0 && <Separator variant={variant} />}
          {i === last ? (
            <span aria-current="page" className={style.current}>
              {item.label}
            </span>
          ) : item.to ? (
            <Link to={item.to} className={style.link} title={item.title}>
              {item.label}
            </Link>
          ) : (
            <span className={style.past} title={item.title}>
              {item.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}
